import React from 'react';
import {
  FacebookFilled, InstagramFilled, LinkedinFilled,
  TikTokFilled, PhoneFilled, MailOutlined,
} from "@ant-design/icons";

/* ─────────────────────────────────────────────────────────────────────────────
   Footer.js  —  matches Home.js design system
   • Dark navy base (--navy) to blend with the CTA section above it
   • Clash Display headings, DM Sans body
   • Gold accent on hover
   • Same grid-based layout, section-label style, CSS variables
───────────────────────────────────────────────────────────────────────────── */

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@400,500,600,700&display=swap');

.flap-footer {
  background: #040e22;
  color: rgba(255,255,255,0.7);
  font-family: 'DM Sans', sans-serif;
  padding: 72px 5% 0;
  position: relative;
  overflow: hidden;
}

/* Subtle grid texture */
.flap-footer::before {
  content: '';
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(28,115,186,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(28,115,186,0.04) 1px, transparent 1px);
  background-size: 52px 52px;
  pointer-events: none;
}

/* Top glow line */
.flap-footer::after {
  content: '';
  position: absolute; top: 0; left: 5%; right: 5%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(28,115,186,0.4), rgba(245,166,35,0.3), transparent);
}

.footer-inner {
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

/* ── Main grid ── */
.footer-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr 1fr;
  gap: 48px;
  padding-bottom: 56px;
}
@media (max-width: 1024px) {
  .footer-grid { grid-template-columns: 1fr 1fr; gap: 36px; }
}
@media (max-width: 580px) {
  .footer-grid { grid-template-columns: 1fr; gap: 32px; }
  .flap-footer { padding-top: 52px; }
}

/* ── Brand column ── */
.footer-brand-logo {
  font-family: 'Clash Display', sans-serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.5px;
  margin-bottom: 14px;
  display: inline-block;
  text-decoration: none;
}
.footer-brand-logo span { color: #f5a623; }

.footer-brand-desc {
  font-size: 0.875rem;
  line-height: 1.75;
  color: rgba(255,255,255,0.5);
  max-width: 280px;
  margin-bottom: 24px;
}

.footer-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(245,166,35,0.1);
  border: 1px solid rgba(245,166,35,0.25);
  border-radius: 99px;
  padding: 5px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #f5a623;
  letter-spacing: 1px;
  text-transform: uppercase;
}
.footer-badge-dot {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: #f5a623;
  animation: footerPulse 2s infinite;
}
@keyframes footerPulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%      { opacity:0.4; transform:scale(0.7); }
}

/* ── Column headings ── */
.footer-col-title {
  font-family: 'Clash Display', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.3px;
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 12px;
}
.footer-col-title::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0;
  width: 24px; height: 2px;
  background: linear-gradient(90deg, #1c73ba, #5aaef7);
  border-radius: 2px;
}

/* ── Links ── */
.footer-links {
  list-style: none;
  padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: 10px;
}
.footer-links li a {
  font-size: 0.875rem;
  color: rgba(255,255,255,0.5);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s, gap 0.2s;
  line-height: 1.4;
}
.footer-links li a::before {
  content: '→';
  font-size: 0.7rem;
  opacity: 0;
  color: #1c73ba;
  transition: opacity 0.2s;
}
.footer-links li a:hover {
  color: rgba(255,255,255,0.9);
  gap: 8px;
}
.footer-links li a:hover::before { opacity: 1; }

/* ── Social icons ── */
.footer-social-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}
.footer-social-btn {
  width: 40px; height: 40px;
  border-radius: 10px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.08);
  display: flex; align-items: center; justify-content: center;
  color: rgba(255,255,255,0.6);
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.2s;
}
.footer-social-btn:hover {
  background: #1c73ba;
  border-color: #1c73ba;
  color: #fff;
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(28,115,186,0.35);
}
.footer-social-btn.phone:hover,
.footer-social-btn.mail:hover {
  background: #f5a623;
  border-color: #f5a623;
  box-shadow: 0 8px 20px rgba(245,166,35,0.35);
}

.footer-contact-row {
  display: flex; flex-direction: column; gap: 8px;
}
.footer-contact-item {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.45);
}
.footer-contact-item a {
  color: rgba(255,255,255,0.55);
  text-decoration: none;
  transition: color 0.2s;
}
.footer-contact-item a:hover { color: #f5a623; }
.footer-contact-icon {
  width: 28px; height: 28px;
  border-radius: 7px;
  background: rgba(255,255,255,0.05);
  display: flex; align-items: center; justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
}

/* ── Bottom bar ── */
.footer-bottom {
  border-top: 1px solid rgba(255,255,255,0.07);
  padding: 20px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.footer-copyright {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.35);
}
.footer-copyright a {
  color: rgba(255,255,255,0.55);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}
.footer-copyright a:hover { color: #f5a623; }

.footer-bottom-links {
  display: flex; gap: 20px;
}
.footer-bottom-links a {
  font-size: 0.78rem;
  color: rgba(255,255,255,0.35);
  text-decoration: none;
  transition: color 0.2s;
}
.footer-bottom-links a:hover { color: rgba(255,255,255,0.7); }

.footer-made {
  font-size: 0.78rem;
  color: rgba(255,255,255,0.25);
  display: flex; align-items: center; gap: 4px;
}
.footer-made span { color: #e25555; }
`;

export default function Footer() {
  const year = new Date().getFullYear();

  const quickLinks = [
    { label: 'Shop',           href: 'https://www.shop.flap.com.np' },
    { label: 'Homepage',       href: 'https://www.flapcard.com.np' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Email Us',       href: 'mailto:card@flap.com.np' },
  ];

  const companyLinks = [
    { label: 'About Flap',  href: '/about-flap' },
    { label: 'Features',    href: '#features' },
    { label: 'How It Works',href: '#how' },
    { label: 'Products',    href: '#accessories' },
  ];

  return (
    <footer className="flap-footer">
      <style>{STYLES}</style>
      <div className="footer-inner">
        <div className="footer-grid">

          {/* ── Brand ── */}
          <div>
            <a href="https://www.flapcard.com.np" className="footer-brand-logo">
              flap<span>.</span>
            </a>
            <p className="footer-brand-desc">
              Built in Nepal, loved globally — Flap bridges culture and technology
              so your digital presence stands out and sticks.
            </p>
            <div className="footer-badge">
              <span className="footer-badge-dot" />
              Nepal's #1 Digital Card
            </div>
          </div>

          {/* ── Quick Links ── */}
          <div>
            <div className="footer-col-title">Quick Access</div>
            <ul className="footer-links">
              {quickLinks.map(l => (
                <li key={l.label}>
                  <a href={l.href} target={l.href.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer">{l.label}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Company ── */}
          <div>
            <div className="footer-col-title">Company</div>
            <ul className="footer-links">
              {companyLinks.map(l => (
                <li key={l.label}><a href={l.href}>{l.label}</a></li>
              ))}
            </ul>
          </div>

          {/* ── Social & Contact ── */}
          <div>
            <div className="footer-col-title">Stay Connected</div>
            <div className="footer-social-grid">
              {[
                { href:'https://www.facebook.com/justtflap',         icon:<FacebookFilled />,  label:'Facebook' },
                { href:'https://www.instagram.com/flap.card/',       icon:<InstagramFilled />, label:'Instagram' },
                { href:'https://www.linkedin.com/company/flapcard/', icon:<LinkedinFilled />,  label:'LinkedIn' },
                { href:'https://www.tiktok.com/@flapcard',           icon:<TikTokFilled />,    label:'TikTok' },
              ].map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                  aria-label={s.label} className="footer-social-btn">
                  {s.icon}
                </a>
              ))}
              <a href="tel:+9779802365432" aria-label="Call Us" className="footer-social-btn phone">
                <PhoneFilled />
              </a>
              <a href="mailto:card@flap.com.np" aria-label="Email" className="footer-social-btn mail">
                <MailOutlined />
              </a>
            </div>

            <div className="footer-contact-row">
              <div className="footer-contact-item">
                <span className="footer-contact-icon"><PhoneFilled /></span>
                <a href="tel:+9779802365432">+977 980-236-5432</a>
              </div>
              <div className="footer-contact-item">
                <span className="footer-contact-icon"><MailOutlined /></span>
                <a href="mailto:card@flap.com.np">card@flap.com.np</a>
              </div>
              <div className="footer-contact-item">
                <span className="footer-contact-icon">📍</span>
                <a href="https://maps.google.com/?q=Kathmandu,Nepal"
                  target="_blank" rel="noopener noreferrer">
                  Kathmandu, Nepal
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* ── Bottom bar ── */}
        <div className="footer-bottom">
          <div className="footer-copyright">
            © {year}{' '}
            <a href="https://www.flap.com.np" target="_blank" rel="noopener noreferrer">
              Flap
            </a>
            {' '}Card — All rights reserved.
          </div>
          <div className="footer-bottom-links">
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy</a>
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms Of Services</a>
            <a href="mailto:hello@flap.com.np">Contact</a>
          </div>
          <div className="footer-made">Made with <span>♥</span> in Nepal</div>
        </div>
      </div>
    </footer>
  );
}